import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { useSession } from '@platform/api-client/react';

import { parseReportSearch, type ReportSearch } from '../../features/reports/range';
import { ReportsHub } from '../../features/reports/reports-hub';
import { REPORT_PATHS, type ReportId } from '../../features/reports/types';
import { useReportQuery } from '../../features/reports/use-report';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/reports/')({
  /**
   * The period lives in the query string so that `/reports?preset=last-month` is a link
   * somebody can send, and so the hub's cards can carry the same period into each report.
   * Anything unrecognised falls back to the last 30 days rather than erroring — a bad
   * `?preset=` is a typo, not a failure.
   */
  validateSearch: (search: Record<string, unknown>): ReportSearch => parseReportSearch(search),
  component: ReportsHubPage,
});

/**
 * `/reports` — the hub (MASTER_PLAN M11.7, FR-ADMIN-10).
 *
 * **No `RequirePermission` around it, on purpose.** The four report keys are separate and
 * a role may hold any subset of them; a guard asking for all four would hide the hub from
 * somebody who can open three of the reports, and a guard asking for one would be
 * arbitrary. So the hub renders for any signed-in staff member and each card is gated
 * individually, with a locked state for the ones the role does not hold and a refusal
 * when it holds none (`features/reports/reports-hub.tsx` says why that is shown rather
 * than hidden). The routes below it *are* guarded, and so is every endpoint.
 */
function ReportsHubPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const session = useSession();

  const now = new Date();
  const { range, groupBy } = useReportQuery(search, now);

  return (
    <ReportsHub
      search={search}
      onSearchChange={(next) => {
        void navigate({ to: '/reports', search: next, replace: true });
      }}
      range={range}
      groupBy={groupBy}
      timeZone={session.data?.tenant?.timeZone ?? 'America/Los_Angeles'}
      now={now}
      can={can}
      renderReportLink={(report: ReportId, children) => (
        <Link
          to={REPORT_PATHS[report]}
          search={search}
          className="inline-flex items-center rounded-button bg-primary-accessible px-4 py-2 text-base font-semibold text-primary-contrast hover:bg-primary-accessible-hover"
        >
          {children}
        </Link>
      )}
    />
  );
}
